/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  Flex,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { useToggle } from '@tonic-one/react-hooks';
import CSpinner from '../../../components/CSpinner';
import { useEffect, useRef, useState } from 'react';
import { Steps } from 'antd';
import { ProvisionOrgInfo, StepIcon } from '../ProvisionModal/share';
import { Trans, useTranslation } from 'react-i18next';
import { deleteCookie, getProvisionCookie } from '@Utils/common';
import { ProvisionApi } from '@Apis';
import { reportPendo } from '@Utils/setupPendo';
import ErrorAlert from '../../../components/Common/ErrorAlert';
import { MultiAppsAuthenticationInfoRes } from '@Types/provision';
import { App, Service } from '@Types/app';
import { CAS_DOC_LINK, getCasOLHLink } from '@Utils/casDocLink';
import i18next from 'i18next';

enum SharePointStatus {
  NotProvision = 0,
  ProvisioningGrantStep1Done = 1,
  ProvisioningGrantStep2Done = 2,
}

const SharePointGraphReAuthModal = ({
  orgId,
  onClose,
  authAccount,
}: {
  orgId: string;
  authAccount: string;
  onClose: () => void;
}) => {
  const { t } = useTranslation('provision');
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);

  const [sharepointStatus, setSharepointStatus] = useState<SharePointStatus>(
    SharePointStatus.NotProvision
  );

  const [authenticationInfo, setAuthenticationInfo] =
    useState<null | MultiAppsAuthenticationInfoRes>(null);

  useEffect(() => {
    if (
      sharepointStatus === SharePointStatus.NotProvision ||
      sharepointStatus === SharePointStatus.ProvisioningGrantStep1Done
    ) {
      ProvisionApi.apiGetMultiAppsReAuthAuthenticationInfos(
        Service.SharePoint,
        orgId
      ).then((res) => {
        setAuthenticationInfo(res.data);
      });
    }
  }, []);

  if (!authenticationInfo) {
    return <></>;
  }
  return (
    <>
      <Modal isOpen={true} isClosable onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {t(`provision.reAuth.service.sharePoint.title`)}
          </ModalHeader>

          <ModalBody>
            <ProvisionOrgInfo orgId={orgId} />

            {errorMsg && (
              <ErrorAlert
                errorMsg={errorMsg}
                onClose={() => setErrorMsg(undefined)}
              />
            )}
            <GrantPermissionSteps
              {...{
                sharepointStatus,
                setSharepointStatus,
                authenticationInfo,
                orgId,
                setErrorMsg,
                authAccount,
              }}
            />
          </ModalBody>
          <ModalFooter>
            <Button minWidth="20x" onClick={onClose}>
              {t('common.button.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default SharePointGraphReAuthModal;

const GrantPermissionSteps = ({
  sharepointStatus,
  setSharepointStatus,
  authenticationInfo,
  orgId,
  setErrorMsg,
  authAccount,
}: {
  sharepointStatus: SharePointStatus;
  setSharepointStatus: (status: SharePointStatus) => void;
  authenticationInfo: MultiAppsAuthenticationInfoRes;
  orgId: string;
  setErrorMsg: (msg: string | undefined) => void;
  authAccount: string;
}) => {
  const { t } = useTranslation(['provision', 'common']);
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const titleTextStyle = {
    fontSize: '16px',
    lineHeight: '22px',
    fontWeight: '400',
    color: colorStyle.color.primary,
  };
  const [loading, toggleLoading] = useToggle(false);
  const timeRef = useRef<any>(null);

  const onFirstPermission = () => {
    const authUrl = authenticationInfo[0].authenticationUrl;
    if (!authUrl) {
      return;
    }
    setErrorMsg(undefined);
    reportPendo('ReAuth.GrantPermission.Click', {
      orgId,
      service: App.SharePoint,
    });
    toggleLoading(true);
    window.open(authUrl, '_blank');
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    timeRef.current = setInterval(async () => {
      const sharepointProvisionInfo = getProvisionCookie(
        'provision_status_azuread_graph_sp'
      );
      if (sharepointProvisionInfo) {
        clearInterval(timeRef.current);
        deleteCookie('provision_status_');
        const { error, code } = JSON.parse(sharepointProvisionInfo);
        if (error === undefined || error.length === 0) {
          try {
            const res = await ProvisionApi.apiSaveReAuthInfo(
              App.SharePoint,
              code,
              authAccount,
              orgId
            );
            if (res.data?.status === 'failure') {
              setErrorMsg(
                t([
                  `provision.service.sharePoint.error.saveInfo.${res.data?.reason}`,
                  'common.apiException.internalError',
                ])
              );
            } else {
              setSharepointStatus(SharePointStatus.ProvisioningGrantStep1Done);
            }
          } catch {
            setErrorMsg(t('common.apiException.internalError'));
          }
        } else {
          setErrorMsg(t('provision.service.sharePoint.error.deniedGrant'));
        }
        toggleLoading(false);
      }
    }, 1000);
  };
  const onSecondPermission = () => {
    const authUrl = authenticationInfo[1].authenticationUrl;
    if (!authUrl) {
      return;
    }
    setErrorMsg(undefined);
    reportPendo('ReAuth.GrantPermission.Click', {
      orgId,
      service: App.SharePointBasic,
    });
    toggleLoading(true);
    window.open(authUrl, '_blank');
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    timeRef.current = setInterval(async () => {
      const sharepointBasicProvisionInfo = getProvisionCookie(
        'provision_status_azuread_graph_basic_sp'
      );
      if (sharepointBasicProvisionInfo) {
        clearInterval(timeRef.current);
        deleteCookie('provision_status_');
        const { error, code } = JSON.parse(sharepointBasicProvisionInfo);
        if (error === undefined || error.length === 0) {
          try {
            const res = await ProvisionApi.apiSaveReAuthInfo(
              App.SharePointBasic,
              code,
              authAccount,
              orgId
            );
            if (res.data?.status === 'failure') {
              setErrorMsg(
                t([
                  `provision.service.sharePoint.error.saveInfo.${res.data?.reason}`,
                  'common.apiException.internalError',
                ])
              );
            } else {
              setSharepointStatus(SharePointStatus.ProvisioningGrantStep2Done);
            }
          } catch {
            setErrorMsg(t('common.apiException.internalError'));
          }
        } else {
          setErrorMsg(t('provision.service.sharePoint.error.deniedGrant'));
        }
        toggleLoading(false);
      }
    }, 1000);
  };

  useEffect(() => {
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    return () => {
      if (timeRef.current) {
        clearInterval(timeRef.current);
      }
    };
  }, []);

  return (
    <>
      {loading && <CSpinner />}
      <Steps
        direction="vertical"
        current={sharepointStatus - 1}
        items={[
          {
            title: (
              <Text {...titleTextStyle}>
                <Trans
                  i18nKey="provision.reAuth.service.sharePoint.details.userConfiguration.STEP1"
                  ns="provision"
                >
                  placeholder
                  <Link
                    href={getCasOLHLink(
                      CAS_DOC_LINK.provision.sharePoint.userConfiguration,
                      i18next.language
                    )}
                    target="_blank"
                  >
                    placeholder
                  </Link>
                  placeholder
                </Trans>
              </Text>
            ),
            description: (
              <Flex direction="column" pt="16px" gap="16px">
                <Box>
                  {sharepointStatus <
                  SharePointStatus.ProvisioningGrantStep1Done ? (
                    <Button
                      variant="secondary"
                      borderRadius="2rem"
                      onClick={() => {
                        onFirstPermission();
                      }}
                    >
                      {t('provision.button.grantPermission')}
                    </Button>
                  ) : (
                    <Text color={colorStyle.color.tertiary}>
                      {t('provision.common.permissionGranted')}
                    </Text>
                  )}{' '}
                </Box>
              </Flex>
            ),
            icon: (
              <StepIcon
                checked={
                  sharepointStatus >=
                  SharePointStatus.ProvisioningGrantStep1Done
                }
              />
            ),
          },
          {
            title: (
              <Text color={colorStyle.color.primary}>
                {t(
                  'provision.reAuth.service.sharePoint.details.userConfiguration.STEP2'
                )}
              </Text>
            ),
            description: (
              <Flex direction="column" pt="16px" gap="16px">
                <Box>
                  {sharepointStatus <
                  SharePointStatus.ProvisioningGrantStep2Done ? (
                    <Button
                      variant="secondary"
                      borderRadius="2rem"
                      disabled={
                        !(
                          sharepointStatus ===
                          SharePointStatus.ProvisioningGrantStep1Done
                        )
                      }
                      onClick={() => {
                        onSecondPermission();
                      }}
                    >
                      {t('provision.button.grantPermission')}
                    </Button>
                  ) : (
                    <Text color={colorStyle.color.tertiary}>
                      {t('provision.common.permissionGranted')}
                    </Text>
                  )}
                </Box>
              </Flex>
            ),
            icon: (
              <StepIcon
                checked={
                  sharepointStatus >=
                  SharePointStatus.ProvisioningGrantStep2Done
                }
              />
            ),
          },
        ]}
      />
    </>
  );
};
